import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ContactCreateForm — #62 Contacts depth spec §3 CREATE, applying the Tasks-loop shape (cmdr,
 * 2026-08-23): minimal-create-then-enrich, header create action opens THIS in the same panel slot
 * CustomerDetails otherwise occupies, so create->detail is one continuous flow, not a second panel.
 *
 * Required: name + one of email/phone ("a contact you cannot reach is not a contact, it is a note").
 * Optional: jobTitle. Company/owner linkage is intentionally NOT in this form — same "field exists,
 * setter UI ships later" pattern as Company.primaryContactId/Account.dealStalenessThresholdDays this
 * session: those relations are read-only in the detail panel until a searchable picker is built.
 */
export const ContactCreateForm: React.FC<{
  onCreated: (leadId: string) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim() && (email.trim() || phone.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/customers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim() || null, phone: phone.trim() || null, jobTitle: jobTitle.trim() || null }),
      });
      if (!res.ok) { setError(t('contactsGrid.createError')); return; }
      const { leadId } = await res.json();
      onCreated(leadId);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="contact-create-form">
      <div className="flex flex-col gap-4 p-4">
        <h2 className="text-lg font-bold text-text-strong">{t('contactsGrid.newContact')}</h2>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('contactsGrid.columns.name')}
            <input autoFocus required data-testid="contact-create-name" value={name} onChange={(e) => setName(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
          </label>
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('contactsGrid.columns.email')}
            <input type="email" data-testid="contact-create-email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm" />
          </label>
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('contactsGrid.phone')}
            <input type="tel" data-testid="contact-create-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm" />
          </label>
          {!canSubmit && (name || email || phone) && (
            <div className="text-xs text-opt-rose" data-testid="contact-create-requirement">{t('contactsGrid.createRequirement')}</div>
          )}
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('contactsGrid.columns.title')}
            <input data-testid="contact-create-jobtitle" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm" dir="auto" />
          </label>
          {error && <div className="text-xs text-opt-rose" data-testid="contact-create-error">{error}</div>}
          <div className="flex items-center gap-2 mt-1">
            <button type="submit" disabled={!canSubmit || submitting} data-testid="contact-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
              {t('contactsGrid.create')}
            </button>
            <button type="button" onClick={onCancel} data-testid="contact-create-cancel"
              className="h-10 px-4 rounded-lg text-sm font-semibold text-text-muted">
              {t('contactsGrid.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
